'use client'

import CopyIcon from '@/components/icons/copy'
import useId from '@/hooks/use-id'
import generateClassNames from '@/utils/generate-class-names'
import clsx from 'clsx'
import mergeRefs from 'merge-refs'
import { forwardRef, useRef } from 'react'
import CopyToClipboard from '../copy-to-clipboard/copy-to-clipboard'
import FormControl from '../form-control'
import FormErrorMessage from '../form-error-message'
import Label from '../label'
import './input-base.scss'

const classes = generateClassNames('input', [
  'field',
  'adornment',
  'separator',
  'error-message',
  'copy-btn',
])

export type InputBaseProps<
  InputComponentType extends React.ElementType = 'input',
> = Omit<React.ComponentPropsWithoutRef<InputComponentType>, 'size'> & {
  controls?: React.ReactNode
  copy?: boolean
  endAdornment?: React.ReactNode
  error?: boolean
  errorMessage?: React.ReactNode
  floatingLabel?: boolean
  inputComponent?: InputComponentType
  label?: React.ReactNode
  size?: 'sm' | 'md' | 'lg'
  startAdornment?: React.ReactNode
  variant?: 'default' | 'tonal'
}

const InputBase = forwardRef(function Input<
  InputElement extends React.ElementType,
>(
  props: InputBaseProps<InputElement>,
  ref: React.ForwardedRef<HTMLInputElement>
) {
  const {
    autoComplete = 'off',
    className,
    controls,
    copy,
    disabled = false,
    endAdornment,
    error = false,
    errorMessage,
    floatingLabel,
    id: idProp,
    inputComponent,
    label,
    required = false,
    size = 'md',
    startAdornment,
    variant = 'default',
    ...other
  } = props

  const inputRef = useRef<HTMLInputElement>(null)
  const handleRef = mergeRefs(inputRef, ref)
  const id = useId(idProp)
  const showErrorMessage = Boolean(error && errorMessage)
  const ariaDescribedby = showErrorMessage ? `${id}-error` : undefined

  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (inputRef.current && event.target !== inputRef.current) {
      event.preventDefault()
      inputRef.current.focus()
    }
  }

  const InputComponent = inputComponent ?? 'input'

  return (
    <FormControl>
      {label ? (
        <Label htmlFor={id} disabled={disabled} required={required}>
          {label}
        </Label>
      ) : null}

      <div
        className={clsx(
          classes.root,
          `input--${size}`,
          `input--${variant}`,
          disabled && 'input--disabled',
          error && 'input--error'
        )}
        
      >
        {startAdornment && (
          <>
            <div className={classes.adornment}>{startAdornment}</div>
            <span className={classes.separator} />
          </>
        )}

        <InputComponent
          autoComplete={autoComplete}
          aria-describedby={ariaDescribedby}
          aria-invalid={error || undefined}
          className={classes.field}
          disabled={disabled}
          id={id}
          ref={handleRef}
          required={required}
          onClick={handleClick}
          {...other}
        />

        {endAdornment && (
          <div className={classes.adornment}>{endAdornment}</div>
        )}

        {copy && !disabled && (
          <CopyToClipboard
            className={classes['copy-btn']}
            text={() => inputRef.current?.value ?? ''}
          >
            <CopyIcon />
          </CopyToClipboard>
        )}

        {controls}
      </div>

      {showErrorMessage ? (
        <FormErrorMessage id={ariaDescribedby}>{errorMessage}</FormErrorMessage>
      ) : null}
    </FormControl>
  )
}) as <InputComponentType extends React.ElementType = 'input'>(
  props: InputBaseProps<InputComponentType> & {
    ref?: React.Ref<HTMLInputElement>
  }
) => React.ReactElement

export default InputBase
